import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { useClampTriggerContext } from '@/hen-ui/hooks/clampTriggerContext';
import { Icon, type IconName } from '@/hen-ui/atomics/Icon';
import {
  InlineCopyContext,
  inheritHostTypeScale,
  isSafeInlineLinkHref,
} from '@/hen-ui/lib/inline-copy';
import { getActivatableProps } from '@/hen-ui/lib/activatable';
import { linkVariants, type LinkVariantProps } from './link.variants';
import {
  cardLinkHrefsMatch,
  useCardLinkContext,
} from '@/hen-ui/hooks/cardLinkContext';
import { cn } from '@/hen-ui/utils';

export interface LinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    LinkVariantProps {
  asChild?: boolean;
  /** When true, the link opens in a new browser tab. */
  openInNewTab?: boolean;
  iconBefore?: IconName;
  iconAfter?: IconName;
  expandedIconAfter?: IconName;
}

/**
 * Link component following HEN Design System standards.
 *
 * A semantic anchor with HEN-specific variants and design tokens.
 * All styling uses tokens from tailwind.config.ts.
 *
 * Inside a link card (`Card` with `href`), a Link to the card's destination
 * renders as a visual-only `<span>`, and a forwarded `ref` receives that span.
 *
 * When used as a LineClamp expansion control, the host is a
 * native button styled with link variants. The forwarded ref follows that
 * host (`HTMLAnchorElement` or `HTMLButtonElement`).
 *
 * @example
 * // Basic usage
 * <Link href="/about">About Us</Link>
 *
 * @example
 * // With HEN variant and size
 * <Link variant="primary" size="lg" href="/contact">
 *   Contact Us
 * </Link>
 *
 * @example
 * // With underline control
 * <Link variant="subtle" underline="always" href="/terms">
 *   Terms & Conditions
 * </Link>
 *
 * @example
 * // With icons (import from lucide-react)
 * import { ExternalLink } from 'lucide-react';
 * <Link variant="primary" href="https://example.com" target="_blank">
 *   Visit Site <ExternalLink />
 * </Link>
 *
 * @example
 * // Without a web address (or with the `#` placeholder), a click action makes
 * // the link a keyboard-operable button (Enter and Space), for example to
 * // open a popup
 * <Link onClick={openDetails}>See details</Link>
 *
 * @example
 * // External link with security best practices
 * <Link href="https://example.com" target="_blank" rel="noopener noreferrer">
 *   External Link
 * </Link>
 */
export const Link = React.forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  LinkProps
>(
  (
    {
      className,
      variant,
      size,
      underline,
      underlineStyle,
      weight,
      iconBefore,
      iconAfter,
      expandedIconAfter,
      children,
      asChild,
      href,
      onClick,
      onKeyDown,
      onKeyUp,
      style,
      openInNewTab,
      target,
      rel,
      ...props
    },
    ref,
  ) => {
    const clampTrigger = useClampTriggerContext();
    const cardLink = useCardLinkContext();
    const nested = React.useContext(InlineCopyContext);
    const safeHref =
      nested && typeof href === 'string' && !isSafeInlineLinkHref(href)
        ? undefined
        : href;
    const resolvedSize = inheritHostTypeScale(nested, size);
    const opensInNewTab =
      openInNewTab === true || (openInNewTab == null && target === '_blank');
    const resolvedTarget = opensInNewTab
      ? '_blank'
      : openInNewTab === false
        ? undefined
        : target;
    const resolvedRel = opensInNewTab
      ? rel || 'noopener noreferrer'
      : openInNewTab === false
        ? undefined
        : rel;
    const Comp = asChild ? Slot : 'a';
    const expanded = clampTrigger?.expanded ?? props['aria-expanded'];
    const isExpanded = expanded === true || expanded === 'true';
    const resolvedIconAfter =
      isExpanded && expandedIconAfter ? expandedIconAfter : iconAfter;
    const renderContent = (content: React.ReactNode) => {
      let replacedText = false;
      const mappedContent = clampTrigger
        ? React.Children.map(content, (child) => {
            if (
              !replacedText &&
              (typeof child === 'string' || typeof child === 'number')
            ) {
              replacedText = true;
              return clampTrigger.label;
            }
            return child;
          })
        : content;
      const labeledContent =
        clampTrigger && !replacedText ? (
          <>
            {clampTrigger.label}
            {mappedContent}
          </>
        ) : (
          mappedContent
        );

      return (
        <>
          {iconBefore && <Icon icon={iconBefore} aria-hidden />}
          {labeledContent}
          {resolvedIconAfter && <Icon icon={resolvedIconAfter} aria-hidden />}
        </>
      );
    };
    const hostChildren =
      asChild &&
      React.isValidElement<
        React.HTMLAttributes<HTMLElement> & { href?: string }
      >(children)
        ? children.props.children
        : children;
    const renderedChildren =
      asChild &&
      !clampTrigger &&
      React.isValidElement<
        React.HTMLAttributes<HTMLElement> & { href?: string }
      >(children)
        ? React.cloneElement(
            children,
            undefined,
            renderContent(children.props.children),
          )
        : renderContent(hostChildren);

    // `#` is the placeholder authoring tools leave when a click action
    // replaces the web address, so it counts as no address too.
    const isActionTrigger =
      !asChild &&
      onClick != null &&
      (safeHref == null || safeHref === '' || safeHref === '#');
    const classNames = cn(
      linkVariants({
        variant,
        size: resolvedSize,
        underline,
        underlineStyle,
        weight,
      }),
      isActionTrigger && 'cursor-pointer',
      className,
    );
    const nestedTypeStyle = nested
      ? { fontSize: 'inherit', lineHeight: 'inherit', ...style }
      : style;

    const demoteLinkToPresentation =
      Boolean(
        cardLink &&
          safeHref &&
          cardLinkHrefsMatch(cardLink.href, safeHref) &&
          !clampTrigger,
      ) && !asChild;

    if (demoteLinkToPresentation) {
      return (
        <span
          className={classNames}
          style={nestedTypeStyle}
          aria-hidden='true'
          ref={ref as React.Ref<HTMLSpanElement>}
        >
          {renderedChildren}
        </span>
      );
    }

    if (clampTrigger) {
      return (
        <button
          type='button'
          className={classNames}
          style={nestedTypeStyle}
          ref={ref as React.Ref<HTMLButtonElement>}
          onClick={(event) => {
            onClick?.(event as unknown as React.MouseEvent<HTMLAnchorElement>);
            event.preventDefault();
            clampTrigger.onToggle();
          }}
          onKeyDown={(event) => {
            onKeyDown?.(
              event as unknown as React.KeyboardEvent<HTMLAnchorElement>,
            );
            if (
              !event.defaultPrevented &&
              (event.key === ' ' || event.key === 'Enter')
            ) {
              event.preventDefault();
              clampTrigger.onToggle();
            }
          }}
          aria-expanded={clampTrigger.expanded}
          aria-controls={clampTrigger.contentId}
          data-hen-id={COMPONENT_IDS.Link}
        >
          {renderContent(hostChildren)}
        </button>
      );
    }

    return (
      <Comp
        className={classNames}
        ref={ref as React.Ref<HTMLAnchorElement>}
        style={nestedTypeStyle}
        {...props}
        href={safeHref}
        onClick={onClick}
        {...(isActionTrigger
          ? getActivatableProps({ onClick, onKeyDown, onKeyUp })
          : { onKeyDown, onKeyUp })}
        target={resolvedTarget}
        rel={resolvedRel}
        data-hen-id={COMPONENT_IDS.Link}
      >
        {renderedChildren}
      </Comp>
    );
  },
);

Link.displayName = 'Link';
